/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faBookOpen } from '@fortawesome/free-solid-svg-icons/faBookOpen';
import { faBullhorn } from '@fortawesome/free-solid-svg-icons/faBullhorn';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome';
import { faHospitalUser } from '@fortawesome/free-solid-svg-icons/faHospitalUser';
import { faGlobe } from '@fortawesome/free-solid-svg-icons/faGlobe';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons/faQuestionCircle';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons/faSignOutAlt';
import { faUsers } from '@fortawesome/free-solid-svg-icons/faUsers';
import { faSortDown } from '@fortawesome/free-solid-svg-icons/faSortDown';
import { faTasks } from '@fortawesome/free-solid-svg-icons/faTasks';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons/faCommentDots';
import { faUserAlt } from '@fortawesome/free-solid-svg-icons/faUserAlt';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons/faUserCircle';
import { faUsersCog } from '@fortawesome/free-solid-svg-icons/faUsersCog';
import {
  catchError,
  delay,
  distinctUntilKeyChanged,
  filter,
  map,
  mergeMap,
  repeat,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { FcmMessageService } from '../../../services/fcm-message/fcm-message.service';
import { HelperService, Language } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { MessageThreadsInterface } from '../../../models/interface/message_threads.interface';
import { NotificationInterface } from '../../../models/interface/notification.interface';
import { BehaviorSubject, forkJoin, fromEvent, Observable, of, Subscription } from 'rxjs';
import { ProfileActionTypes, removeProfileStore, removeProfileRolesStore } from '../../../store/profile/profile.action';
import { RoleInterface } from '../../../models/interface/role.interface';
import { Actions, ofType } from '@ngrx/effects';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { AuthenticationActionTypes, forcedLogoutStore } from '../../../store/authentication/authentication.action';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { MessageStore } from '../../../store/message/component-store/message.store';
import { ProfileStore } from '../../../store/profile/component-store/profile.store';
import { CustomCookieService } from 'src/app/services/customcookie/custom-cookie.service';
import { MyProfileService } from 'src/app/services/api/my-profile/my-profile.service';
import { ActivityInterface } from 'src/app/models/interface/activity.interface';
import { UserStore } from 'src/app/store/user/component-store/user.store';
import { HelperActivityService } from 'src/app/services/helper/helper-activity/helper-activity.service';
import { UserSharedService } from 'src/app/services/shared/user-shared/user-shared.service';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  providers: [InterventionInstanceStore, MessageStore, ProfileStore, UserStore],
})
export class NavigationComponent implements OnInit, OnDestroy {
  public title = 'eSano - eCoach';

  // Icons
  faSignOutAlt = faSignOutAlt;
  faUserAlt = faUserAlt;
  faUserCircle = faUserCircle;
  faHome = faHome;
  faTasks = faTasks;
  faGlobe = faGlobe;
  faClock = faClock;
  faBookOpen = faBookOpen;
  faUsers = faUsers;
  faPen = faPen;
  faCommentDots = faCommentDots;
  faBell = faBell;
  faHospitalUser = faHospitalUser;
  faQuestionCircle = faQuestionCircle;
  faBullhorn = faBullhorn;
  faSortDown = faSortDown;
  faDownload = faDownload;
  faUsersCog = faUsersCog;

  // Authentication status
  public isAuthenticated$: Observable<boolean>;

  public isLoginSubject = new BehaviorSubject<boolean>(false);

  public profile$: Observable<UserInterface>;
  public profileRoles$: Observable<RoleInterface[]>;

  public isAdminSubject = new BehaviorSubject<boolean>(false);

  // Contains notification for navbar
  public myNotifications: NotificationInterface[] = [];

  private feedbackForParticipants: MessageThreadsInterface[] = [];

  public hasPendingFeedback = false;
  public unreadFeedback = 0;
  public unreadConversations = 0;

  public showBanner = null;

  /**
   * Used to avoid double call from general initiation (ngOnInit) and loading pages (NavigationStart)
   */
  private threadsAreRequested = false;
  private isAuthed = false;

  private inboxFolder$: Observable<MessageThreadsInterface[]>;
  private inboxFolderAnswersheet$: Observable<MessageThreadsInterface[]>;

  private instance$: Observable<InterventionInstanceInterface>;

  private threadDetails$: Observable<MessageThreadsInterface>;

  private unlockMyInstancesAsEcoachResponse$: Observable<any>;

  private updateProfileResponse$: Observable<any>;

  private memberTasks$: Observable<ActivityInterface[]>;

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private helperActivityService: HelperActivityService,
    private translateService: TranslateService,
    private fcmMessageService: FcmMessageService,
    private customCookieService: CustomCookieService,
    private router: Router,
    private interventionInstanceStore: InterventionInstanceStore,
    private messageStore: MessageStore,
    private myProfileService: MyProfileService,
    private userSharedService: UserSharedService,
    private profileStore: ProfileStore,
    private userStore: UserStore,
    private store: Store<{
      myProfile: ProfileInterface;
      myRoles: RoleInterface[];
      isAuthenticated: boolean;
    }>,
    private actions$: Actions,
  ) {
    this.subscriptions.push(
      this.router.events.pipe(filter((event: Event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
        this.isLoginSubject.next(event.url === '/login');
      }),
    );
    this.isAuthenticated$ = this.store.select('isAuthenticated');
    this.profile$ = this.store.select('myProfile');
    this.profileRoles$ = this.store.select('myRoles');
    this.instance$ = this.interventionInstanceStore.instance$;
    this.unlockMyInstancesAsEcoachResponse$ = this.interventionInstanceStore.unlockMyInstancesAsEcoachResponse$;
    this.inboxFolder$ = this.messageStore.inboxFolder$;
    this.inboxFolderAnswersheet$ = this.messageStore.inboxFolderAnswersheet$;
    this.threadDetails$ = this.messageStore.threadDetails$;
    this.updateProfileResponse$ = this.profileStore.updateProfileResponse$;
    this.memberTasks$ = this.userStore.memberTasks$;
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  ngOnInit(): void {
    const source$ = fromEvent<StorageEvent>(window, 'storage');
    this.subscriptions.push(
      source$
        .pipe(
          filter((value) => value.key === 'isAuthenticated'),
          distinctUntilKeyChanged('newValue'),
          switchMap((_value: StorageEvent) => {
            if (!this.customCookieService.isCookieValid()) {
              this.store.dispatch(forcedLogoutStore({ manualLogout: false }));
            } else {
              this.updateMessageCount();
              const base64 = window.atob(localStorage.getItem('myProfile'));
              const myProfile = JSON.parse(base64);
              return this.profile$.pipe(
                take(1),
                switchMap((profile: UserInterface) => {
                  if (JSON.stringify(profile) !== JSON.stringify(myProfile)) {
                    this.store.dispatch({ type: ProfileActionTypes.getProfileType });
                    this.store.dispatch({ type: ProfileActionTypes.getProfileRolesType });
                    this.router.navigate(['/']);
                  }
                  return of(true);
                })
              );
            }
            return of(true);
          }),
        )
        .subscribe({}),
    );

    // Get authentication state and profile
    this.subscriptions.push(
      this.isAuthenticated$
        .pipe(
          mergeMap((value) => {
            if (value) {
              this.isAuthed = value;
            }
            return of(value);
          }),
        )
        .subscribe((value) => {
          if (value) {
            this.store.dispatch({ type: ProfileActionTypes.getProfileType });
            this.store.dispatch({ type: ProfileActionTypes.getProfileRolesType });
            this.store.dispatch({ type: ProfileActionTypes.getProfileActivitiesType, payload: { action: undefined, unlimited: true } });
            this.updateMessageCount();
          } else {
            this.logout(false);
          }
        }),
    );
    const unlockAllInstances = () => {
      this.interventionInstanceStore.unlockMyInstancesAsEcoach({});
      return this.unlockMyInstancesAsEcoachResponse$.pipe(filter((value) => !!value), take(1));
    };

    const poll = of({}).pipe(
      mergeMap((_) => unlockAllInstances()),
      catchError((_e) => of(null)),
      tap(() => console.log(new Date())),
      delay(1800000),
      repeat(),
    );

    poll.subscribe();

    this.subscriptions.push(
      this.profileRoles$.pipe(filter((roles) => roles.length > 0)).subscribe({
        next: (roles) => {
          this.isAdminSubject.next(this.helperService.hasRoles(roles, /admin$/));
        },
        error: () => {
          this.isAdminSubject.next(false);
        },
      }),
    );

    // Translation
    const language = this.getLocalLanguage();
    if (this.helper.isLanguage(language)) {
      this.translateService.use(language);
    }

    // Notification created when notification to thread is non-existent otherwise summarize multiple notifications with a counter
    this.subscriptions.push(
      this.fcmMessageService.currentMessage.subscribe((value: any) => {
        if (value) {
          this.updateMessageCount();
          const notificationJson = this.helperService.getJSONObject(value.notification.body);
          let notificationType: 'ANSWERSHEET' | 'INTERVENTION_INSTANCE' | 'THREAD' | 'FEEDBACK';
          // Set notification type
          if (notificationJson) {
            const reqs: any = [];
            this.messageStore.getInboxFolder({});
            reqs.push(this.inboxFolder$.pipe(skip(1), take(1)));
            this.messageStore.getInboxFolder({ answersheetIdFlag: true });
            reqs.push(this.inboxFolderAnswersheet$.pipe(skip(1), take(1)));
            forkJoin(reqs).subscribe((result) => {
              let filterThread: MessageThreadsInterface[] = [];
              filterThread = filterThread.concat(result[0]);
              filterThread = filterThread.concat(result[1]);
              const found: MessageThreadsInterface = filterThread.find(
                (thread: MessageThreadsInterface) => thread.id.toString() === notificationJson.thread_id.toString()
              );
              notificationType = found && found.attributes.answersheet_id !== null ? 'FEEDBACK' : 'THREAD';
              this.pushNotification(value, notificationType);
            });
          } else {
            if (value.notification.body.includes('ANSWERSHEET_SUBMITTED')) {
              notificationType = 'ANSWERSHEET';
            } else {
              notificationType = 'INTERVENTION_INSTANCE';
            }
            this.pushNotification(value, notificationType);
          }
        }
      })
    );

    // URLs that trigger reloading threads.
    // Always reload when navigating to home or when leaving pages that mark threads as read.
    const toURLS = ['/home', '/feedback-overview'] as const;
    const fromURLS = ['/feedback-overview/answersheets/', '/conversations?'] as const;
    this.subscriptions.push(
      this.router.events
        .pipe(
          filter((event: Event) => event instanceof NavigationStart),
        )
        .subscribe(
          (event: NavigationStart) => {
            const to = event.url;
            const toURLtriggersReload = toURLS.some((url) => to.startsWith(url));
            const from = this.router.getCurrentNavigation()?.previousNavigation?.initialUrl.toString();
            const fromURLtriggeringReload = from && fromURLS.find((url) => from.startsWith(url));
            // Skip reloading threads while navigating within URL that triggers reload.
            // E.g. changing conversations uses navigation and triggers this code.
            const skipReload = fromURLtriggeringReload && to.startsWith(fromURLtriggeringReload);
            if (!skipReload && (toURLtriggersReload || fromURLtriggeringReload)) {
              this.updateMessageCount();
            }
          }
        ),
    );
  }

  public setPlatformLanguage(language: Language): void {
    if (this.translateService.currentLang === language) {
      return;
    }
    const previousLocalLanguage = this.getLocalLanguage();
    this.setLocalLanguage(language);
    this.translateService.use(language);
    this.myProfileService.getMyProfile().subscribe((profile) => {
      const settings = {
        ...profile.body.data.attributes?.settings,
        locale: language,
      };
      this.profileStore.updateProfile({ settings });
      this.updateProfileResponse$.pipe(skip(1), take(1)).subscribe({
        error: () => {
          // Synchronize local state with backend if language wasn't saved
          if (this.helper.isLanguage(previousLocalLanguage)) {
            this.setLocalLanguage(previousLocalLanguage);
          }
        },
      });
    });
  }

  private getLocalLanguage() {
    return localStorage.getItem('language');
  }

  private setLocalLanguage(language: Language) {
    localStorage.setItem('language', language);
  }

  isSelectedLanguage(language: Language): boolean {
    return this.translateService.currentLang === language;
  }

  /**
   * This function sends a logout request.
   */
  public logout(manualLogout: boolean): void {
    this.store.dispatch({ type: AuthenticationActionTypes.logoutType });
    this.subscriptions.push(
      this.actions$
        .pipe(ofType(AuthenticationActionTypes.logoutSuccessType, AuthenticationActionTypes.logoutErrorType), take(1))
        .subscribe(() => {
          this.store.dispatch(forcedLogoutStore({ manualLogout }));
        })
    );
  }

  /**
   * This function returns translation to notification.
   *
   * @return string A string needed for ngx-translation
   */
  public getNotificationTranslation(notification: NotificationInterface): string {
    if (notification.data.type === 'THREAD') {
      if (notification.count === 1) {
        return 'navbar.notification_you_have_a_new_message';
      } else {
        return 'navbar.notification_you_have_new_messages';
      }
    }
    if (notification.data.type === 'FEEDBACK') {
      return notification.data.subject;
    }
    if (notification.data.type === 'ANSWERSHEET') {
      return 'navbar.answersheet_submitted';
    }
    if (notification.data.type === 'INTERVENTION_INSTANCE') {
      if (notification.count === 1) {
        return 'navbar.intervention_instance_updated';
      } else {
        return 'navbar.intervention_instance_updated_multiple';
      }
    }
    return '';
  }

  /**
   * This function returns an notification object.
   *
   * @return Object An object that defines how many notifications belong to a subject
   */
  public getNotificationParams(notification: NotificationInterface) {
    if (notification.count === 1) {
      return { thread_name: notification.data.subject };
    } else {
      return {
        thread_name: notification.data.subject,
        count: notification.count,
      };
    }
  }

  /**
   * This function redirects to the component
   * TODO Implement redirections to component depending on the content of the notification
   *
   * @return string A string needed for ngx-translation
   */
  public redirectToComponent(notification: NotificationInterface): void {
    switch (notification.data.type) {
      case 'ANSWERSHEET': {
        this.router.navigate([`/feedback-overview/answersheets/${notification.data.answersheet_id}`]);
        break;
      }
      case 'THREAD': {
        this.router.navigate(['/conversations'], { queryParams: { thread_id: notification.data.thread_id } });
        break;
      }
      case 'FEEDBACK': {
        this.messageStore.getThreadDetails({ threadId: notification.data.thread_id });
        this.subscriptions.push(
          this.threadDetails$.pipe(skip(1), take(1)).subscribe((message) => {
            this.router.navigateByUrl(`feedback-overview/answersheets/${message.attributes.answersheet_id}`);
          }),
        );
        break;
      }
      case 'INTERVENTION_INSTANCE': {
        this.interventionInstanceStore.getInstance(notification.data.intervention_instance_id);
        this.subscriptions.push(
          this.instance$
            .pipe(
              filter((value) => !!value),
              take(1),
            )
            .subscribe((instance) => {
              this.router.navigate([`/patients/${instance.attributes.patient_id}/instances`]);
            }),
        );
        break;
      }
      default: {
        break;
      }
    }
  }

  private updateMessageCount(): void {
    if (this.isAuthed && !this.threadsAreRequested) {
      this.threadsAreRequested = true;
      this.userSharedService.pushConversations(null);
      this.userSharedService.pushFeedback(null);
      this.userSharedService.pushTasks(null);
      this.messageStore.getInboxFolder({ include: 'participants' });
      const conversations = this.inboxFolder$.pipe(
        skip(1),
        take(1),
        map((result) => {
          this.userSharedService.pushConversations(result);
          return result;
        }),
      );
      this.messageStore.getInboxFolder({ answersheetIdFlag: true });
      const feedback = this.inboxFolderAnswersheet$.pipe(
        skip(1),
        take(1),
        map((result) => {
          this.userSharedService.pushFeedback(result);
          return result;
        }),
      );
      this.userStore.getTasksOfMembers({
        activityFilter: 'ANSWERSHEET_SUBMITTED,INTERVENTION_INSTANCE_CAN_UNLOCK',
        finished: '0,pending',
        feedbackRequired: 1,
        include: 'questionnaires',
      });
      const tasks = this.memberTasks$.pipe(skip(1), take(1));
      forkJoin({ feedback, conversations, tasks }).subscribe((result) => {
        this.feedbackForParticipants = result.feedback.filter((thread) => thread.attributes?.answersheet_id !== null);
        this.hasPendingFeedback = this.helperActivity.hasPendingFeedback(result.tasks, this.feedbackForParticipants);
        this.unreadFeedback = this.getUnreadThreads(this.feedbackForParticipants);
        this.unreadConversations = this.getUnreadThreads(result.conversations);
        this.userSharedService.pushTasks(result.tasks);
        this.threadsAreRequested = false;
      });
    }
  }

  private getUnreadThreads(threads: MessageThreadsInterface[]): number {
    let unreadThreads: number = 0;
    for (const thread of threads) {
      if (thread.attributes?.unread.is_unread && thread.attributes.unread.messages > 0) {
        unreadThreads++;
      }
    }
    return unreadThreads;
  }

  public pushNotification(
    value: NotificationInterface,
    notificationType: 'ANSWERSHEET' | 'INTERVENTION_INSTANCE' | 'THREAD' | 'FEEDBACK'
  ): void {
    const jsonObject = this.helperService.getJSONObject(value.notification.body);
    if (jsonObject) {
      const info: { thread_id: string; subject: string; answersheet_id: number } = jsonObject;

      const findWithAttr = (array: NotificationInterface[], anonValue: string): number => {
        for (let i = 0; i < array.length; i += 1) {
          if (!anonValue) {
            return -1;
          }
          if (array[i].data.thread_id.toString() === anonValue.toString()) {
            return i;
          }
        }
        return -1;
      };

      const notificationIndex: number = findWithAttr(this.myNotifications, info.thread_id);

      if (notificationIndex !== -1) {
        this.myNotifications[notificationIndex].count++;
      } else {
        let notificationInstance: NotificationInterface;

        const answersheetSubject = 'ANSWERSHEET_SUBMITTED';
        if (value.notification.body.includes('FEEDBACK_REQUIRED:TRUE') || value.notification.body.includes('ANSWERSHEET_SUBMITTED')) {
          const answersheetId = parseInt(
            value.notification.title.replace('ANSWERSHEET_SUBMITTED:', '').replace('FEEDBACK_REQUIRED:TRUE', ''),
            10
          );
          notificationInstance = {
            data: {
              thread_id: value.data.thread_id,
              subject: answersheetSubject,
              intervention_instance_id: value.data.intervention_instance_id,
              type: notificationType,
              answersheet_id: answersheetId,
            },
            from: value.from,
            priority: value.priority,
            count: 1,
          };
          this.myNotifications.push(notificationInstance);
        } else {
          notificationInstance = {
            data: {
              thread_id: jsonObject.thread_id,
              subject: jsonObject.subject,
              intervention_instance_id: -1,
              type: notificationType,
            },
            from: value.from,
            priority: value.priority,
            count: 1,
          };
          this.myNotifications.push(notificationInstance);
        }
      }
    } else {
      const id = parseInt(value.notification.title, 10);
      const findWithAttr = (array: NotificationInterface[], anonValue: { id: number, body: string }): number => {
        for (let i = 0; i < array.length; i += 1) {
          if (!anonValue) {
            return -1;
          }
          if (
            array[i].data.intervention_instance_id.toString() === anonValue.id.toString() &&
            anonValue.body.includes('INTERVENTION_INSTANCE')
          ) {
            return i;
          }
        }
        return -1;
      };
      const notificationIndex = findWithAttr(this.myNotifications, { id, body: value.notification.body });
      if (notificationIndex !== -1) {
        this.myNotifications[notificationIndex].count++;
      } else {
        let notificationInstance: NotificationInterface;
        if (value.notification.body.includes('ANSWERSHEET_SUBMITTED')) {
          const answersheetId = parseInt(value.notification.title, 10);
          notificationInstance = {
            data: {
              thread_id: null,
              subject: 'navbar.answersheet_submitted',
              intervention_instance_id: null,
              type: notificationType,
              answersheet_id: answersheetId
            },
            from: value.from,
            priority: value.priority,
            count: 1
          };
          this.myNotifications.push(notificationInstance);
        } else if (value.notification.body.includes('INTERVENTION_INSTANCE')) {
          const instanceId = parseInt(value.notification.title, 10);
          notificationInstance = {
            data: {
              thread_id: jsonObject.thread_id,
              subject: 'navbar.intervention_instance_updated',
              intervention_instance_id: instanceId,
              type: notificationType
            },
            from: value.from,
            priority: value.priority,
            count: 1
          };
          this.myNotifications.push(notificationInstance);
        }
      }
    }
  }

  public redirectToHome(): void {
    this.router.navigate(['/']);
  }

  public redirectToAdmin(): void {
    this.router.navigate(['/admin']);
  }

  public displayAnnouncements(): void {
    this.showBanner = !this.showBanner;
  }

  public removeProfile(): void {
    this.store.dispatch(removeProfileStore());
    this.store.dispatch(removeProfileRolesStore());
  }

  public openDialogDownloadReport(): void {
    this.helperDialog
      .openDialogDownloads()
      .afterClosed()
      .subscribe(() => {});
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
